<template>
  <el-drawer
    :visible.sync="localVisible"
    direction="rtl"
    size="800px" 
    :wrapperClosable="true"
    :with-header="false"
    custom-class="hotel-detail-drawer"
    append-to-body
  >
    <div v-if="hotel" class="detail-container" v-loading="loading">
      
      <!-- 1. 顶部沉浸式 Banner -->
      <div class="hero-banner">
        <el-image :src="computedImageUrl" fit="cover" class="hero-image">
          <div slot="error" class="image-slot"><i class="el-icon-picture-outline"></i> 暂无首图</div>
        </el-image>
        <div class="hero-overlay">
          <div class="hero-content">
            <h1 class="hotel-title">{{ hotel.HotelNameCn }}</h1>
            <div class="hotel-badges">
              <el-tag size="small" effect="dark" type="warning">{{ hotel.Star }}星级</el-tag>
              <el-tag size="small" effect="dark" v-if="hotel.isMain" color="#ffd700" style="color:#333; border:none">主推</el-tag>
            </div>
          </div>
        </div>
        <div class="close-btn" @click="localVisible = false"><i class="el-icon-close"></i></div>
      </div>

      <div class="detail-body">
        <!-- 2. 财务风险提示 -->
        <div v-if="hotel.walletBalance > 0" class="wallet-alert-card">
          <div class="alert-icon">💰</div>
          <div class="alert-content">
            <div class="alert-title">重点消化推荐</div>
            <div class="alert-desc">该酒店钱包剩余 <b>¥{{ hotel.walletBalance.toLocaleString() }}</b>，请优先引导预订以降低财务风险。</div>
          </div>
        </div>

        <!-- 3. 基础信息 -->
        <div class="section-card info-grid">
          <div class="info-item address-row">
            <i class="el-icon-location"></i>
            <span>{{ hotel.AddressCn || '地址暂无' }}</span>
          </div>
          
          <div class="info-item">
            <i class="el-icon-place"></i>
            <span>距 <b>{{ hotel.targetHallName }}</b> {{ (hotel.distance / 1000).toFixed(1) }} km</span>
          </div>

          <div class="info-item" v-if="hotel.ReceptionPhone">
            <i class="el-icon-phone-outline"></i>
            <span>前台：<b>{{ hotel.ReceptionPhone }}</b></span>
          </div>

          <div class="info-item" v-if="hotel.FloorPrice">
            <i class="el-icon-price-tag"></i>
            <span>平时参考：<b class="reference-price">¥{{ hotel.FloorPrice }}</b></span>
          </div>

          <div class="info-item" v-if="hotel.HotelRoomNum">
            <i class="el-icon-office-building"></i>
            <span>总客房数：<b>{{ hotel.HotelRoomNum }}</b> 间</span>
          </div>

          <div class="info-item" v-if="hotel.OpeningYear || hotel.DecorationYear">
            <i class="el-icon-date"></i>
            <span>
              <template v-if="hotel.OpeningYear">{{ hotel.OpeningYear }}开业</template>
              <template v-if="hotel.DecorationYear"> / {{ hotel.DecorationYear }}装修</template>
            </span>
          </div>
        </div>

        <!-- 3.5 酒店备注 -->
        <div class="section-card remark-section" v-if="hotel.Remark">
          <div class="section-header"><i class="el-icon-notebook-2"></i><span>酒店备注</span></div>
          <div class="remark-content">{{ hotel.Remark }}</div>
        </div>

        <!-- 4. 酒店标签/收款政策 -->
        <div class="section-card hotel-tags" v-if="hasTags">
          <div class="tag-list">
            <div class="tag-item collect-tag" v-if="hotel.CollectType">
              <i class="el-icon-wallet"></i>
              <span>收款：<b>{{ formatCollectType(hotel.CollectType) }}</b></span>
            </div>
            <div class="tag-item" v-if="hotel.IsForeign">
              <i class="el-icon-user"></i><span style="color: #67c23a;">接待外宾</span>
            </div>
            <div class="tag-item" v-if="hotel.HotelTaxPoints">
              <i class="el-icon-coin"></i><span>税点 <b>{{ hotel.HotelTaxPoints }}%</b></span>
            </div>
            <div class="tag-item" v-if="hotel.HotelDevelopersName">
              <i class="el-icon-user-solid"></i><span>BD：{{ hotel.HotelDevelopersName }}</span>
            </div>
          </div>
        </div>

        <!-- 5. 资料资源 -->
        <div class="section-card" v-if="realVideoUrls.length > 0 || realPptUrl">
          <div class="section-header">资料</div>
          <div class="media-row">
            <div class="media-item" v-for="(video, index) in realVideoUrls" :key="'v'+index">
              <div class="media-icon video"><i class="el-icon-video-camera-solid"></i></div>
              <div class="media-info">
                <div class="name">宣传视频 {{ index + 1 }}</div>
                <div class="actions">
                  <el-link type="primary" :href="video.url" target="_blank">在线观看</el-link>
                  <el-divider direction="vertical"></el-divider>
                  <el-link type="info" @click="handleDownload(video.url, `video_${index+1}.mp4`)">下载</el-link>
                </div>
              </div>
            </div>
            <div class="media-item" v-if="realPptUrl">
              <div class="media-icon ppt"><i class="el-icon-document"></i></div>
              <div class="media-info">
                <div class="name">介绍PPT/PDF</div>
                <div class="actions">
                  <el-link type="primary" :href="realPptUrl" target="_blank">预览</el-link>
                  <el-divider direction="vertical"></el-divider>
                  <el-link type="info" @click="handleDownload(realPptUrl, 'intro.pdf')">下载</el-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 6. 实时房态 -->
        <div class="section-card">
          <div class="section-header">
            <span>实时房态</span>
            <el-tag size="mini" type="info" effect="plain">价格含服务费</el-tag>
          </div>
          <div v-for="(roomType, rIndex) in hotel.Room" :key="rIndex" class="room-block">
            <div class="room-title"><span>{{ roomType.RoomType }}</span></div>
            <el-table :data="getFilteredRooms(roomType.Rooms)" border size="small" :header-cell-style="{background:'#f5f7fa', color:'#606266'}">
              <el-table-column label="日期" width="90" align="center">
                <template slot-scope="scope"><b>{{ formatDate(scope.row.CheckDate) }}</b></template>
              </el-table-column>
              <el-table-column label="卖价" width="95" align="center">
                <template slot-scope="scope"><span class="price-highlight">¥{{ scope.row.Price }}</span></template>
              </el-table-column>
              <el-table-column label="利润率" width="85" align="center">
                <template slot-scope="scope"><span :class="getProfitClass(scope.row)" class="profit-text">{{ calculateProfitRate(scope.row) }}</span></template>
              </el-table-column>
              <el-table-column label="网络价" width="85" align="center">
                <template slot-scope="scope"><span class="price-strike">¥{{ scope.row.CtrIpPrice }}</span></template>
              </el-table-column>
              <el-table-column label="库存" width="85" align="center">
                <template slot-scope="scope">
                  <el-tag v-if="scope.row.AvailableRoom > 5" type="success" size="mini" effect="plain">余 {{ scope.row.AvailableRoom }}</el-tag>
                  <el-tag v-else-if="scope.row.AvailableRoom > 0" type="danger" size="mini" effect="plain">剩 {{ scope.row.AvailableRoom }}</el-tag>
                  <el-tag v-else type="info" size="mini">满房</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="标签" min-width="150">
                <template slot-scope="scope">
                   <div class="tag-wrap">
                     <span v-if="scope.row.IsBus" class="mini-tag bus">{{scope.row.BusTypeStr}}</span>
                     <span v-if="scope.row.IsBreakfast" class="mini-tag food">{{scope.row.BreakfastTypeStr}}</span>
                     <span v-if="STOCK_TYPE_MAP[scope.row.StockType] && scope.row.StockType!==0" class="mini-tag stock" :class="'type-'+scope.row.StockType">{{ STOCK_TYPE_MAP[scope.row.StockType] }}</span>
                     <span v-if="scope.row.Status == 2" class="mini-tag status"><i class="el-icon-warning-outline"></i> 下架</span>
                   </div>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import { getOssSignedUrl, downloadFile } from '../utils/ossService';
import { STOCK_TYPE_MAP } from '../utils/hotel-logic';

export default {
  name: 'HotelDetailDrawer',
  props: {
    visible: Boolean,
    hotel: Object,
    filterDateRange: Array
  },
  data() {
    return { STOCK_TYPE_MAP, loading: false, realVideoUrls: [], realPptUrl: '' }
  },
  computed: {
    localVisible: {
      get() { return this.visible; },
      set(val) { this.$emit('update:visible', val); }
    },
    computedImageUrl() {
      return this.hotel?.DefaultImage ? `https://mxapi.mxydt.com/Upload/${this.hotel.DefaultImage}` : '';
    },
    hasTags() {
      return !!(this.hotel?.IsForeign || this.hotel?.HotelTaxPoints || this.hotel?.HotelDevelopersName || this.hotel?.CollectType);
    }
  },
  watch: {
    hotel: {
      handler(newVal) {
        if (newVal) this.fetchMediaResources();
      },
      immediate: true
    }
  },
  methods: {
    formatCollectType(t) {
      return { 1: '酒店代收', 2: '酒店部分收', 3: '盟轩收' }[t] || '其他';
    },
    async fetchMediaResources() {
      this.loading = true;
      try {
        const videoPath = this.hotel.IntroductionVideoPath;
        if (videoPath) {
          const paths = videoPath.split(',').filter(p => p.trim());
          const urls = await Promise.all(paths.map(p => getOssSignedUrl(p.trim())));
          this.realVideoUrls = urls.map(u => ({ url: u }));
        }
        this.realPptUrl = await getOssSignedUrl(this.hotel.IntroductionPPTPath);
      } catch (e) { console.error(e); } finally { this.loading = false; }
    },
    handleDownload(url, name) { if(url) downloadFile(url, name); },
    formatDate(d) { return d ? d.split('T')[0].substring(5) : ''; },
    calculateProfitRate(room) {
      const sales = room.ExhibitionPrice || room.Price;
      if (!sales) return '-';
      const cost = room.LowPrice || 0;
      const bus = (room.IsBus && room.BusType !== 2) ? 40 : 0;
      return (((sales - cost - bus) / sales) * 100).toFixed(2) + '%';
    },
    getProfitClass(room) {
      const rate = parseFloat(this.calculateProfitRate(room));
      return rate >= 20 ? 'profit-high' : (rate < 5 ? 'profit-low' : 'profit-normal');
    },
    getFilteredRooms(rooms) {
      if (!this.filterDateRange?.length) return rooms;
      return rooms.filter(r => {
        const d = r.CheckDate?.split('T')[0];
        return d >= this.filterDateRange[0] && d <= this.filterDateRange[1];
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.detail-container {
  height: 100%; display: flex; flex-direction: column; background: #f5f7fa; overflow-y: auto; font-family: "Helvetica Neue",Helvetica,"PingFang SC","Hiragino Sans GB","Microsoft YaHei","微软雅黑",Arial,sans-serif;
}

.hero-banner {
  position: relative; height: 220px; flex-shrink: 0; background: #333;
  .hero-image { width: 100%; height: 100%; opacity: 0.85; }
  .image-slot { display: flex; justify-content: center; align-items: center; height: 100%; background: #e0e0e0; color: #909399; }
  .hero-overlay { position: absolute; bottom: 0; left: 0; width: 100%; height: 100%; background: linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.85) 100%); display: flex; align-items: flex-end; padding: 20px; box-sizing: border-box; }
  .hero-content { color: #fff; .hotel-title { font-size: 24px; margin: 0 0 10px 0; text-shadow: 0 2px 4px rgba(0,0,0,0.5); } .hotel-badges { display: flex; gap: 8px; } }
  .close-btn { position: absolute; top: 15px; right: 15px; width: 32px; height: 32px; background: rgba(0,0,0,0.4); border-radius: 50%; color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 10; &:hover { background: rgba(0,0,0,0.7); } }
}

.detail-body { padding: 16px; flex: 1; }

.section-card {
  background: #fff; border-radius: 8px; padding: 16px; margin-bottom: 16px; box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  .section-header { font-size: 16px; font-weight: bold; color: #303133; margin-bottom: 16px; padding-left: 10px; border-left: 4px solid #1890ff; display: flex; justify-content: space-between; align-items: center; }
}

.wallet-alert-card {
  background: #fff1f0; border: 1px solid #ffccc7; border-radius: 8px; padding: 15px; display: flex; align-items: flex-start; margin-bottom: 16px;
  .alert-icon { font-size: 24px; margin-right: 12px; }
  .alert-content { .alert-title { color: #cf1322; font-weight: bold; font-size: 15px; margin-bottom: 4px; } .alert-desc { color: #5c0011; font-size: 13px; line-height: 1.5; b { font-size: 14px; color: #cf1322; } } }
}

.info-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px 20px;
  .info-item {
    display: flex; align-items: center; font-size: 14px; color: #606266;
    i { color: #909399; font-size: 16px; margin-right: 8px; flex-shrink: 0; }
    b { color: #303133; margin: 0 2px; }
    &.address-row { grid-column: 1 / -1; }
    .reference-price { color: #409EFF; font-weight: bold; }
  }
}

.remark-section {
  background: #fffdf2; border-left: 4px solid #f39c12;
  .section-header { border-left: none; color: #e67e22; margin-bottom: 8px; }
  .remark-content { font-size: 14px; line-height: 1.6; color: #5e6d82; white-space: pre-wrap; }
}

.hotel-tags {
  .tag-list { display: flex; flex-wrap: wrap; gap: 10px; }
  .tag-item {
    display: flex; align-items: center; background: #f4f7f9; border: 1px solid #e1e7ec; padding: 5px 12px; border-radius: 16px; font-size: 13px; color: #606266;
    i { margin-right: 6px; color: #409eff; }
    &.collect-tag { border-color: #d9ecff; background: #ecf5ff; }
    b { margin-left: 2px; color: #303133; font-weight: 600; }
  }
}

.media-row {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  .media-item {
    display: flex; align-items: center; background: #fafafa; border-radius: 6px; padding: 10px; border: 1px solid #f0f0f0;
    .media-icon { width: 36px; height: 36px; border-radius: 6px; display: flex; align-items: center; justify-content: center; margin-right: 10px; color: #fff; font-size: 18px; &.video { background: #36cfc9; } &.ppt { background: #ff7a45; } }
    .media-info { .name { font-size: 13px; font-weight: bold; color: #333; margin-bottom: 2px; } .actions { font-size: 12px; } }
  }
}

.room-block {
  margin-bottom: 20px;
  .room-title { background: #f0f4f8; padding: 6px 12px; border-radius: 4px; font-weight: bold; font-size: 14px; margin-bottom: 10px; display: inline-block; color: #333; }
  .price-highlight { color: #f5222d; font-weight: bold; font-size: 15px; }
  .price-strike { color: #999; text-decoration: line-through; font-size: 12px; }
  .profit-text { font-weight: bold; }
  .profit-high { color: #f5222d; }
  .profit-low { color: #999; }
  .tag-wrap {
    display: flex; gap: 4px; flex-wrap: wrap;
    .mini-tag {
      font-size: 11px; padding: 1px 4px; border-radius: 2px; line-height: 1.4;
      &.bus { color: #13c2c2; background: #e6fffb; border: 1px solid #b5f5ec; }
      &.food { color: #fa8c16; background: #fff7e6; border: 1px solid #ffd591; }
      &.stock { color: #2f54eb; background: #f0f5ff; border: 1px solid #adc6ff; &.type-1 { color: #f5222d; background: #fff1f0; border-color: #ffa39e; } }
      &.status { color: #8c8c8c; background: #f5f5f5; border: 1px solid #d9d9d9; }
    }
  }
}
</style>